import { useEffect, useRef } from 'react';
import { FlowObserverPanelEngine } from '../engine/FlowObserverPanelEngine.js';
import { FlowFooterEngine } from '../engine/FlowFooterEngine.js';
import type { ExecutionStatusData, ExecutionLogEntry } from '../types/tab-data.types.js';
import type { ExecutionEventInfo } from '../types/execution-event.types.js';
import { useDesignerModeStore } from '@flow-studio/store';

interface ExecutionEventDetail {
  messageId: string;
  type: string;
  data: Record<string, unknown>;
  eventInfo?: ExecutionEventInfo; // EventInfo from backend envelope
  item?: Record<string, unknown>;
  status?: Record<string, unknown>;
  progress?: Record<string, unknown>;
  error?: Record<string, unknown>;
  dataEnvelope?: {
    data?: Record<string, unknown>;
    eventInfo?: ExecutionEventInfo;
    metadata?: Record<string, unknown>;
  };
}

let _logIdSeq = 0;
function nextLogId(): string {
  return `log-${Date.now()}-${++_logIdSeq}`;
}

/**
 * Listens to the window 'execution-event' CustomEvents dispatched by useExecutionSignalR
 * and feeds the data into FlowObserverPanelEngine and FlowFooterEngine.
 * Mount once at WorkflowEditor level alongside useExecutionSignalR.
 */
export function useFlowObserverSignalR(): void {
  const currentStatusRef = useRef<ExecutionStatusData | null>(null);
  const errorCountRef = useRef<number>(0);
  const startTimeRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks whether the first WorkflowStarted for this execution has been received.
  // Prevents subsequent thread starts (multi-thread workflows) from clearing accumulated logs.
  const hasReceivedFirstWorkflowStartedRef = useRef<boolean>(false);

  useEffect(() => {
    const handleExecutionEvent = (e: Event) => {
      const detail = (e as CustomEvent<ExecutionEventDetail>).detail;
      console.log('[useFlowObserverSignalR] Raw event detail from server:', JSON.stringify(detail, null, 2));

      // Extract eventInfo from nested dataEnvelope if present, otherwise from top level
      const eventInfo = detail.dataEnvelope?.eventInfo ?? detail.eventInfo;
      const { type, data } = detail;

      // Backend sends real progress metrics on the event payload (data.progress).
      // Prefer these over locally-tracked counters so nodesTotal is never stuck at 0.
      const progress = data?.progress as
        | { totalNodes?: number; completedNodes?: number; failedNodes?: number; skippedNodes?: number }
        | undefined;

      // subStageID (a raw GUID like "20000004-0000-0000-0000-000000000015") used to be appended
      // here, making every message extremely long — and it's redundant now that Stage/SubStage/
      // Description are their own grid columns, so it's dropped rather than shown twice.
      const buildStagePart = (stage?: string, subStage?: string, subStageDescription?: string): string => {
        if (!stage) return '';
        const sub = subStage ? `/${subStage}` : '';
        const desc = subStageDescription ? `: ${subStageDescription}` : '';
        return ` › [${stage}${sub}]${desc}`;
      };

      switch (type) {
        case 'WorkflowStarted':
        case 'ReceiveWorkflowStarted': {
          const isFirstThread = !hasReceivedFirstWorkflowStartedRef.current;
          hasReceivedFirstWorkflowStartedRef.current = true;

          errorCountRef.current = 0;
          if (isFirstThread) startTimeRef.current = Date.now();

          FlowObserverPanelEngine.setTabEngineState('execution-status', 'available');
          FlowObserverPanelEngine.setTabEngineState('execution-logs', 'available');
          // Only clear logs on the FIRST thread start. Subsequent threads in a multi-thread
          // process would otherwise erase events already accumulated from earlier threads.
          if (isFirstThread) {
            FlowObserverPanelEngine.clearData('execution-logs');
            FlowObserverPanelEngine.clearData('log-detail');
          }
          // Open execution-logs and execution-status so they appear in the tab bar,
          // then activate dashboard last so it becomes the focused tab — but only
          // auto-focus before the user has ever manually picked a tab this session.
          // Once they have, re-running the workflow must not yank them away from
          // wherever they are (§5.1). activateTab() does "add to open tabs" and
          // "bring to front" as one inseparable store operation, so all three calls
          // share the gate, and dashboard (activated last) wins focus.
          // This mirrors TopToolbar.handleExecute's synchronous activateTab('dashboard')
          // — that call happens on click, before this event arrives over SignalR, so
          // without also landing on 'dashboard' here, this handler used to race it and
          // clobber the tab back to execution-status a moment after execution started.
          if (!FlowObserverPanelEngine.hasUserSelectedTab()) {
            FlowObserverPanelEngine.activateTab('execution-logs');
            FlowObserverPanelEngine.activateTab('execution-status');
            FlowObserverPanelEngine.activateTab('dashboard');
          }

          FlowFooterEngine.updateBadge('execution-status', {
            labelKey: 'flowFooter.execution.running',
            fallbackLabel: 'Running',
            state: 'running',
          });

          // Start elapsed timer
          if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = setInterval(() => {
            if (startTimeRef.current === null) return;
            const elapsedMs = Date.now() - startTimeRef.current;
            const snapshot: Partial<ExecutionStatusData> = {
              ...(currentStatusRef.current ?? { runState: 'running', nodesTotal: 0, nodesCompleted: 0, nodesFailed: 0 }),
              elapsedMs,
            };
            FlowObserverPanelEngine.pushData('execution-status', snapshot);
          }, 1000);

          const status: ExecutionStatusData = {
            runState: 'running',
            nodesTotal: progress?.totalNodes ?? 0,
            nodesCompleted: progress?.completedNodes ?? 0,
            nodesFailed: progress?.failedNodes ?? 0,
            skippedNodes: progress?.skippedNodes,
            startedAt: new Date().toISOString(),
            eventInfo, // Include event context in unified data structure
          };
          currentStatusRef.current = status;
          FlowObserverPanelEngine.pushData('execution-status', status);

          const wfItemName = String(data?.item && typeof data.item === 'object' && 'name' in data.item ? (data.item as { name?: string }).name : '') || 'Unknown';
          const wfDataObj = data as { stage?: string; subStage?: string; subStageDescription?: string; subStageID?: string } | undefined;
          const wfStagePart = buildStagePart(wfDataObj?.stage, wfDataObj?.subStage, wfDataObj?.subStageDescription);
          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: 'info',
            message: `Workflow started: ${wfItemName}${wfStagePart}`,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          break;
        }

        case 'NodeExecuting':
        case 'ReceiveNodeExecuting': {
          const item = data?.item as { key?: string; id?: unknown; name?: string } | undefined;
          const nodeId = item?.key ?? String(item?.id ?? '');
          const nodeLabel = item?.name ?? '';
          const status: ExecutionStatusData = {
            runState: 'running',
            currentNodeId: nodeId,
            currentNodeLabel: nodeLabel,
            elapsedMs: startTimeRef.current ? Date.now() - startTimeRef.current : 0,
            nodesTotal: progress?.totalNodes ?? currentStatusRef.current?.nodesTotal ?? 0,
            nodesCompleted: progress?.completedNodes ?? currentStatusRef.current?.nodesCompleted ?? 0,
            nodesFailed: progress?.failedNodes ?? currentStatusRef.current?.nodesFailed ?? 0,
            skippedNodes: progress?.skippedNodes ?? currentStatusRef.current?.skippedNodes,
            eventInfo, // Include event context
          };
          currentStatusRef.current = status;
          FlowObserverPanelEngine.pushData('execution-status', status);

          const neDataObj = data as { stage?: string; subStage?: string; subStageDescription?: string; subStageID?: string } | undefined;
          const neStagePart = buildStagePart(neDataObj?.stage, neDataObj?.subStage, neDataObj?.subStageDescription);
          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: 'info',
            sourceNodeId: nodeId,
            sourceNodeLabel: nodeLabel,
            message: `Node executing: ${nodeLabel || nodeId}${neStagePart}`,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          if (nodeId) useDesignerModeStore.getState().updateNodeStatus(nodeId, { status: 'running', ...(nodeLabel ? { label: nodeLabel } : {}) });
          break;
        }

        case 'NodeExecuted':
        case 'ReceiveNodeExecuted': {
          const item = data?.item as { key?: string; id?: unknown; name?: string } | undefined;
          const statusData = data?.status as { name?: string } | undefined;
          const nodeId = item?.key ?? String(item?.id ?? '');
          const nodeLabel = item?.name ?? '';
          const prevStatus = currentStatusRef.current;
          const status: ExecutionStatusData = {
            runState: 'running',
            nodesTotal: progress?.totalNodes ?? prevStatus?.nodesTotal ?? 0,
            nodesCompleted: progress?.completedNodes ?? (prevStatus?.nodesCompleted ?? 0) + 1,
            nodesFailed: progress?.failedNodes ?? prevStatus?.nodesFailed ?? 0,
            skippedNodes: progress?.skippedNodes ?? prevStatus?.skippedNodes,
            eventInfo, // Include event context
          };
          currentStatusRef.current = status;
          FlowObserverPanelEngine.pushData('execution-status', status);

          // Same fix as the NodeProgressing case below: this event can represent either a
          // successful or a failed completion, but the log entry's level was hardcoded to
          // 'info' regardless — a node failing via this event type never surfaced as an error.
          // Backend's ProcessEngineHubMessageStatus.OnComplete() always sets Name="Completed"
          // for success (never "Success" literal) and OnError() sets Name="Errored" — checking
          // for '=== success' here meant this branch was always false, marking every healthy
          // node completion as an error.
          const succeeded = !statusData?.name || !['errored', 'failed'].includes(statusData.name.toLowerCase());
          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: succeeded ? 'info' : 'error',
            sourceNodeId: nodeId,
            sourceNodeLabel: nodeLabel,
            message: `Node completed: ${nodeLabel || nodeId} (${statusData?.name ?? 'Success'})`,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          if (nodeId) {
            useDesignerModeStore.getState().updateNodeStatus(nodeId, { status: succeeded ? 'success' : 'failed', ...(nodeLabel ? { label: nodeLabel } : {}) });
          }
          break;
        }

        case 'NodeProgressing': {
          const item = data?.item as { key?: string; id?: unknown; name?: string } | undefined;
          const nodeId = item?.key ?? String(item?.id ?? '');
          const nodeLabel = item?.name ?? '';
          const npDataObj = data as { stage?: string; subStage?: string; subStageDescription?: string; subStageID?: string } | undefined;
          const npStage = npDataObj?.stage ?? (eventInfo?.eventDescription as { stage?: string } | undefined)?.stage;
          const npSubStage = npDataObj?.subStage ?? (eventInfo?.eventDescription as { subStage?: string } | undefined)?.subStage;
          const npStagePart = buildStagePart(npStage, npSubStage, npDataObj?.subStageDescription);
          // This was hardcoded to 'debug' regardless of outcome — a node whose only terminal
          // signal is a 'progressing' event with stage 'Error' (the same convention
          // useExecutionSignalR.ts uses to mark the node "failed" in nodeExecutionStates) would
          // never produce a level==='error' log entry, so it was invisible to Error Analysis and
          // to the Execution Logs Level filter even though Execution Status correctly counted it
          // as failed.
          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: npStage === 'Error' ? 'error' : 'debug',
            sourceNodeId: nodeId,
            sourceNodeLabel: nodeLabel,
            message: `Progressing: ${nodeLabel || nodeId}${npStagePart}`,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          break;
        }

        case 'ExecutionError':
        case 'ReceiveExecutionError': {
          const errorData = data?.error as { errorMessage?: string } | undefined;
          const item = data?.item as { key?: string; id?: unknown; name?: string } | undefined;
          const errorMsg = errorData?.errorMessage ?? String(data?.message ?? 'Unknown error');
          const nodeId = item?.key ?? String(item?.id ?? '');
          const nodeLabel = item?.name ?? '';

          errorCountRef.current += 1;
          FlowObserverPanelEngine.setTabBadge('execution-logs', errorCountRef.current);

          const prevStatus = currentStatusRef.current;
          const status: ExecutionStatusData = {
            runState: 'failed',
            nodesTotal: progress?.totalNodes ?? prevStatus?.nodesTotal ?? 0,
            nodesCompleted: progress?.completedNodes ?? prevStatus?.nodesCompleted ?? 0,
            nodesFailed: progress?.failedNodes ?? (prevStatus?.nodesFailed ?? 0) + 1,
            skippedNodes: progress?.skippedNodes ?? prevStatus?.skippedNodes,
            errorMessage: errorMsg,
            eventInfo, // Include event context
          };
          currentStatusRef.current = status;
          FlowObserverPanelEngine.pushData('execution-status', status);

          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: 'error',
            sourceNodeId: nodeId,
            sourceNodeLabel: nodeLabel,
            message: errorMsg,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          if (nodeId) useDesignerModeStore.getState().updateNodeStatus(nodeId, { status: 'failed', ...(nodeLabel ? { label: nodeLabel } : {}) });
          break;
        }

        case 'WorkflowCancelled':
        case 'ExecutionCancelled':
        case 'ReceiveWorkflowCancelled': {
          hasReceivedFirstWorkflowStartedRef.current = false;
          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
          }

          const prevStatus = currentStatusRef.current;
          const status: ExecutionStatusData = {
            runState: 'cancelled',
            nodesTotal: progress?.totalNodes ?? prevStatus?.nodesTotal ?? 0,
            nodesCompleted: progress?.completedNodes ?? prevStatus?.nodesCompleted ?? 0,
            nodesFailed: progress?.failedNodes ?? prevStatus?.nodesFailed ?? 0,
            skippedNodes: progress?.skippedNodes ?? prevStatus?.skippedNodes,
            completedAt: new Date().toISOString(),
            elapsedMs: startTimeRef.current ? Date.now() - startTimeRef.current : 0,
            eventInfo, // Include event context
          };
          currentStatusRef.current = status;
          FlowObserverPanelEngine.pushData('execution-status', status);

          FlowFooterEngine.updateBadge('execution-status', {
            labelKey: 'flowFooter.execution.cancelled',
            fallbackLabel: 'Cancelled',
            state: 'error',
          });

          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: 'warn',
            message: 'Execution cancelled',
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          break;
        }

        case 'WorkflowCompleted':
        case 'ProcessCompleted':
        case 'ReceiveWorkflowCompleted': {
          hasReceivedFirstWorkflowStartedRef.current = false;
          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
          }

          const statusName = (data?.status as { name?: string } | undefined)?.name ?? 'Completed';
          const runState =
            statusName === 'Failed' || statusName === 'Errored' ? 'failed' :
            statusName === 'Cancelled' ? 'cancelled' : 'completed';

          const prevStatus = currentStatusRef.current;
          // On completion, the backend's final progress snapshot is authoritative — prefer it
          // so nodesTotal reflects the real node count instead of the locally-tracked value
          // (which is 0 unless every intermediate event happened to carry a progress payload).
          const finalNodesTotal = progress?.totalNodes ?? prevStatus?.nodesTotal ?? 0;
          const finalNodesCompleted =
            progress?.completedNodes ??
            (runState === 'completed' && finalNodesTotal > 0
              ? finalNodesTotal
              : prevStatus?.nodesCompleted ?? 0);
          const status: ExecutionStatusData = {
            runState,
            nodesTotal: finalNodesTotal,
            nodesCompleted: finalNodesCompleted,
            nodesFailed: progress?.failedNodes ?? prevStatus?.nodesFailed ?? 0,
            skippedNodes: progress?.skippedNodes ?? prevStatus?.skippedNodes,
            completedAt: new Date().toISOString(),
            elapsedMs: startTimeRef.current ? Date.now() - startTimeRef.current : 0,
            eventInfo, // Include event context
          };
          currentStatusRef.current = status;
          FlowObserverPanelEngine.pushData('execution-status', status);

          FlowFooterEngine.updateBadge('execution-status', {
            labelKey: runState === 'completed' ? 'flowFooter.execution.idle' : 'flowFooter.execution.error',
            fallbackLabel: runState === 'completed' ? 'Idle' : 'Error',
            state: runState === 'completed' ? 'idle' : 'error',
          });

          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: runState === 'completed' ? 'info' : 'error',
            message: `Execution ${statusName.toLowerCase()}`,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          break;
        }

        // Terminal-for-now status when a workflow suspends (HIL) or is paused (admin) — previously
        // silently dropped server-side (ProcessEngineEventService's routing switch had no case for
        // ProcessThread+Executing), so a workflow ending its run suspended for HIL showed nothing
        // after "Workflow starting" (Binoy, 2026-09-24: "workflow log should tell the final status").
        case 'WorkflowWaiting':
        case 'ReceiveWorkflowWaiting':
        case 'WorkflowPaused':
        case 'ReceiveWorkflowPaused': {
          const isPaused = type === 'WorkflowPaused' || type === 'ReceiveWorkflowPaused';
          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
          }

          const prevStatus = currentStatusRef.current;
          const status: ExecutionStatusData = {
            runState: isPaused ? 'paused' : 'waiting',
            nodesTotal: progress?.totalNodes ?? prevStatus?.nodesTotal ?? 0,
            nodesCompleted: progress?.completedNodes ?? prevStatus?.nodesCompleted ?? 0,
            nodesFailed: progress?.failedNodes ?? prevStatus?.nodesFailed ?? 0,
            skippedNodes: progress?.skippedNodes ?? prevStatus?.skippedNodes,
            elapsedMs: startTimeRef.current ? Date.now() - startTimeRef.current : 0,
            eventInfo,
          };
          currentStatusRef.current = status;
          FlowObserverPanelEngine.pushData('execution-status', status);

          FlowFooterEngine.updateBadge('execution-status', {
            labelKey: isPaused ? 'flowFooter.execution.paused' : 'flowFooter.execution.waiting',
            fallbackLabel: isPaused ? 'Paused' : 'Waiting',
            state: 'running',
          });

          const message = (data?.message as string | undefined)
            ?? (isPaused ? 'Workflow paused' : 'Workflow suspended — waiting for input');
          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: 'info',
            message,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          break;
        }

        case 'ExecutionCatchup': {
          // Fired by the catch-up code when WorkflowStarted was missed (race condition).
          // Set startTimeRef from the server's UTC startedAt so future NodeExecuting events
          // compute elapsedMs from the correct origin, and restart the 1-second ticker.
          const rawStartedAt = data?.startedAt as string | undefined;
          if (rawStartedAt) {
            const utcStr = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(rawStartedAt) ? rawStartedAt : rawStartedAt + 'Z';
            startTimeRef.current = new Date(utcStr).getTime();
            if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = setInterval(() => {
              if (startTimeRef.current === null) return;
              const elapsedMs = Date.now() - startTimeRef.current;
              const snapshot: Partial<ExecutionStatusData> = {
                ...(currentStatusRef.current ?? { runState: 'running', nodesTotal: 0, nodesCompleted: 0, nodesFailed: 0 }),
                elapsedMs,
              };
              FlowObserverPanelEngine.pushData('execution-status', snapshot);
            }, 1000);
          }
          break;
        }

        case 'ProcessStarted': {
          const item = data?.item as { name?: string; entityType?: string } | undefined;
          const psDataObj = data as { stage?: string; subStage?: string; subStageDescription?: string; subStageID?: string } | undefined;
          const psStagePart = buildStagePart(psDataObj?.stage, psDataObj?.subStage, psDataObj?.subStageDescription);
          const logEntry: ExecutionLogEntry = {
            id: nextLogId(),
            timestamp: new Date().toISOString(),
            level: 'info',
            message: `${item?.entityType ?? 'Process'} started: ${item?.name ?? 'Unknown'}${psStagePart}`,
            eventInfo,
            rawDetail: data,
          };
          FlowObserverPanelEngine.pushData('execution-logs', logEntry);
          break;
        }

        default:
          break;
      }
    };

    const handleClearLogs = (e: Event) => {
      const detail = (e as CustomEvent<{ tabId: string }>).detail;
      if (detail?.tabId === 'execution-logs') {
        errorCountRef.current = 0;
      }
    };

    window.addEventListener('execution-event', handleExecutionEvent);
    window.addEventListener('flow-observer-clear-logs', handleClearLogs);

    return () => {
      window.removeEventListener('execution-event', handleExecutionEvent);
      window.removeEventListener('flow-observer-clear-logs', handleClearLogs);
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    };
  }, []);
}
